// sound5/instruments.js: instruments rendered procedurally into AudioBuffers
// (additive synthesis with recursive oscillators), the first time they are
// needed or from an idle-time prefetch queue. Playing a note is then one
// AudioBufferSourceNode: no per-sample JavaScript at play time.
import { mtof } from './util.js';

const TAU = Math.PI * 2;
const perf = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

// Adds an exponentially decaying sine (amplitude `amp`, time constant `tau` s)
// using y[n] = 2cos(w) y[n-1] - y[n-2]; stops once it is 80 dB down.
function addPartial(out, sr, f, amp, tau, phase) {
  const w = TAU * f / sr, c = 2 * Math.cos(w);
  let y1 = Math.sin(phase - w), y2 = Math.sin(phase - 2 * w);
  const d = Math.exp(-1 / (tau * sr));
  const end = Math.min(out.length, Math.ceil(tau * sr * 9.3));
  let a = amp;
  for (let i = 0; i < end; i++) {
    const y = c * y1 - y2;
    y2 = y1;
    y1 = y;
    out[i] += a * y;
    a *= d;
  }
}

// Raised-cosine attack and a cosine fade to exact silence at the end.
function shape(out, sr, atk, rel) {
  const na = Math.max(1, Math.floor(atk * sr)), nr = Math.max(1, Math.floor(rel * sr)), n = out.length;
  for (let i = 0; i < na && i < n; i++) out[i] *= 0.5 - 0.5 * Math.cos(Math.PI * i / na);
  for (let i = 0; i < nr && i < n; i++) out[n - 1 - i] *= 0.5 - 0.5 * Math.cos(Math.PI * i / nr);
}

function finish(ctx, out, peak, sr) {
  let m = 0;
  for (let i = 0; i < out.length; i++) { const a = Math.abs(out[i]); if (a > m) m = a; }
  const s = m > 0 ? peak / m : 0;
  const buf = ctx.createBuffer(1, out.length, sr || ctx.sampleRate), d = buf.getChannelData(0);
  for (let i = 0; i < out.length; i++) d[i] = out[i] * s;
  return buf;
}

function lowNoise(out, sr, rng, amp, tau, fc) {
  const a = 1 - Math.exp(-TAU * fc / sr), n = Math.min(out.length, Math.floor(tau * sr * 8));
  let l1 = 0, l2 = 0;
  for (let i = 0; i < n; i++) {
    l1 += a * ((rng() * 2 - 1) - l1);
    l2 += a * (l1 - l2);
    out[i] += l2 * amp * Math.exp(-i / (tau * sr));
  }
}

// Felt piano (Arnalds / Jóhannsson): muffled hammer spectrum, slightly
// stretched partials, two strings beating on the lowest partials, a soft thud.
export function renderPiano(ctx, midi, rng, sr = ctx.sampleRate) {
  const f0 = mtof(midi), len = Math.floor(sr * 3.2);
  const out = new Float32Array(len);
  const B = 0.00035, baseT = 2.4 * Math.pow(262 / f0, 0.4);
  for (let n = 1; n <= 14; n++) {
    const fn = n * f0 * Math.sqrt(1 + B * n * n);
    if (fn > 6000) break;
    const amp = Math.pow(n, -1.25) * Math.exp(-(n - 1) * 0.3) * (0.35 + 0.65 * Math.abs(Math.sin(Math.PI * n * 0.13)));
    const tau = baseT / (1 + 0.55 * (n - 1));
    if (n <= 3) {
      const k = Math.pow(2, 0.9 / 1200);
      addPartial(out, sr, fn * k, amp * 0.5, tau, rng() * TAU);
      addPartial(out, sr, fn / k, amp * 0.5, tau * 0.9, rng() * TAU);
    } else addPartial(out, sr, fn, amp, tau, rng() * TAU);
  }
  lowNoise(out, sr, rng, 0.9, 0.007, 700);
  shape(out, sr, 0.003, 0.4);
  return finish(ctx, out, 0.9, sr);
}

// Small glass bell: inharmonic partials, pairs slightly detuned so they shimmer.
// Partials above 7.2 kHz are skipped (no harshness on phone speakers).
export function renderBell(ctx, midi, rng, sr = ctx.sampleRate) {
  const f0 = mtof(midi), len = Math.floor(sr * 2.8);
  const out = new Float32Array(len);
  const R = [1, 2.0, 2.76, 4.07, 5.43], A = [1, 0.2, 0.4, 0.15, 0.07], T = [2.1, 1.4, 0.95, 0.5, 0.28];
  const k = Math.pow(880 / f0, 0.35);
  for (let i = 0; i < R.length; i++) {
    const f = f0 * R[i];
    if (f > 7200) continue;
    addPartial(out, sr, f * 1.0006, A[i] * 0.5, T[i] * k, rng() * TAU);
    addPartial(out, sr, f * 0.9994, A[i] * 0.5, T[i] * k * 0.88, rng() * TAU);
  }
  shape(out, sr, 0.0015, 0.3);
  return finish(ctx, out, 0.9, sr);
}

// Heartbeat thump: a pitched-down sine kick, softly saturated so its
// harmonics carry on small speakers.
export function renderThump(ctx, sr = ctx.sampleRate) {
  const len = Math.floor(sr * 0.45), out = new Float32Array(len);
  let ph = 0;
  const k = Math.tanh(1.8);
  for (let i = 0; i < len; i++) {
    const t = i / sr, f = 41 + 36 * Math.exp(-t / 0.03);
    ph += TAU * f / sr;
    const env = (1 - Math.exp(-t / 0.004)) * Math.exp(-t / 0.085);
    out[i] = Math.tanh(1.8 * Math.sin(ph) * env) / k;
  }
  shape(out, sr, 0.001, 0.08);
  return finish(ctx, out, 0.95, sr);
}

// A soft wooden tick (the Interstellar clock), short and below 4 kHz.
export function renderTick(ctx, rng, sr = ctx.sampleRate) {
  const len = Math.floor(sr * 0.09), out = new Float32Array(len);
  addPartial(out, sr, 1180, 0.6, 0.012, 0);
  addPartial(out, sr, 2210, 0.3, 0.006, 0);
  addPartial(out, sr, 3350, 0.1, 0.003, 0);
  lowNoise(out, sr, rng, 0.5, 0.0015, 3000);
  shape(out, sr, 0.0005, 0.02);
  return finish(ctx, out, 0.9, sr);
}

// Reverse swell for the ignition: a struck D add9 chord's ring-out plus a
// noise wash, reversed so it grows like reversed reverb into a hard cut.
export function renderSwell(ctx, rng, sr = ctx.sampleRate, notes = [50, 57, 62, 64, 66, 69, 74], seconds = 1.8) {
  const len = Math.floor(sr * seconds), out = new Float32Array(len);
  for (const m of notes) {
    const f = mtof(m);
    for (let n = 1; n <= 4; n++) {
      const fn = f * n;
      if (fn > 5000) break;
      addPartial(out, sr, fn * (1 + (rng() - 0.5) * 0.002), 0.5 / n, (seconds * 0.45) / Math.sqrt(n), rng() * TAU);
    }
  }
  lowNoise(out, sr, rng, 2.2, seconds * 0.3, 2400);
  out.reverse();
  shape(out, sr, 0.25, 0.012);
  return finish(ctx, out, 0.9, sr);
}

// Buffers are synthesised at `sr` (24-32 kHz is plenty: every partial is
// below 7.2 kHz) and resampled by the source node, which halves memory and
// render time on phones.
export class Bank {
  constructor(ctx, rng, sr) {
    this.ctx = ctx;
    this.rng = rng;
    this.sr = sr || ctx.sampleRate;
    this.map = new Map();
    this.queue = [];
  }
  get(kind, midi = 0) {
    const key = kind + ':' + midi;
    let b = this.map.get(key);
    if (!b) {
      const c = this.ctx, r = this.rng, sr = this.sr;
      if (kind === 'piano') b = renderPiano(c, midi, r, sr);
      else if (kind === 'bell') b = renderBell(c, midi, r, sr);
      else if (kind === 'thump') b = renderThump(c, sr);
      else if (kind === 'swell') b = renderSwell(c, r, sr);
      else b = renderTick(c, r, sr);
      this.map.set(key, b);
    }
    return b;
  }
  want(kind, list) {
    for (const m of list) this.queue.push([kind, m]);
  }
  // Render queued buffers for at most ~budget ms (at least one per call).
  work(budget) {
    const t0 = perf();
    while (this.queue.length) {
      const [k, m] = this.queue.shift();
      if (!this.map.has(k + ':' + m)) this.get(k, m);
      if (perf() - t0 > budget) break;
    }
  }
}
